import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Download, Pencil } from "lucide-react";
import { fmtTime, fmtDate, fmtDateTime, romeDayKey } from "@/lib/format";

const hm = (min) => `${Math.floor((min || 0) / 60)}h ${(min || 0) % 60}m`;
const iso = (d) => d.toISOString().slice(0, 10);
const today = new Date();
const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

export function AttendanceTab({ team, canWrite }) {
  const staff = team.filter((t) => ["stylist", "assistant", "manager", "reception"].includes(t.role));
  const nameById = Object.fromEntries(team.map((t) => [t.id, t.name]));
  const [from, setFrom] = useState(iso(monthStart));
  const [to, setTo] = useState(iso(today));
  const [operatorId, setOperatorId] = useState("all");
  const [sheet, setSheet] = useState(null);
  const [records, setRecords] = useState([]);
  const [editRec, setEditRec] = useState(null);

  const load = useCallback(async () => {
    setSheet(null);
    const op = operatorId !== "all" ? `&operator_id=${operatorId}` : "";
    try {
      const [s, r] = await Promise.all([
        api.get(`/team/attendance-sheet?from=${from}&to=${to}${op}`),
        api.get(`/attendance?from=${from}&to=${to}${op}`),
      ]);
      setSheet(s.data.rows); setRecords(r.data);
    } catch (e) { toast.error("Errore caricamento presenze"); setSheet([]); }
  }, [from, to, operatorId]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const header = ["Collaboratore", "Ruolo", "Ore programmate (min)", "Ore effettive (min)", "Pausa (min)", "Differenza (min)"];
    const lines = (sheet || []).map((r) => [r.operator_name, r.role, r.programmed_minutes, r.effective_minutes, r.break_minutes, r.diff_minutes].join(";"));
    const blob = new Blob([[header.join(";"), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `presenze_${from}_${to}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const openEdit = (r) => setEditRec({
    ...r,
    edit_date: r.date || (r.clock_in ? romeDayKey(r.clock_in) : (r.clock_out ? romeDayKey(r.clock_out) : "")),
    in_time: r.clock_in ? fmtTime(r.clock_in) : "",
    out_time: r.clock_out ? fmtTime(r.clock_out) : "",
    reason: "",
  });

  const saveCorrection = async () => {
    if (editRec.in_time && editRec.out_time && editRec.out_time <= editRec.in_time) {
      toast.error("L'uscita deve essere successiva all'entrata"); return;
    }
    try {
      await api.put(`/attendance/${editRec.id}`, {
        date: editRec.edit_date,
        clock_in_time: editRec.in_time || "",
        clock_out_time: editRec.out_time || "",
        reason: (editRec.reason || "").trim(),
      });
      toast.success("Timbratura corretta"); setEditRec(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-5">
      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1"><Label className="text-xs">Dal</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[150px]" data-testid="att-from" /></div>
          <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[150px]" data-testid="att-to" /></div>
          <div className="space-y-1"><Label className="text-xs">Collaboratore</Label>
            <Select value={operatorId} onValueChange={setOperatorId}><SelectTrigger className="w-[200px]" data-testid="att-operator"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tutti</SelectItem>{staff.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
          <Button variant="outline" onClick={exportCsv} data-testid="att-export"><Download className="h-4 w-4 mr-1" /> Esporta CSV</Button>
        </div>
      </Card>

      <Card className="p-0">
        <div className="p-3 font-heading text-lg font-semibold">Foglio presenze</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Collaboratore</TableHead><TableHead>Programmate</TableHead>
            <TableHead>Effettive</TableHead><TableHead>Pause</TableHead>
            <TableHead>Differenza</TableHead><TableHead>Assenze</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sheet === null && Array.from({ length: 4 }).map((_, i) => <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
            {sheet && sheet.map((r) => (
              <TableRow key={r.operator_id} data-testid={`sheet-row-${r.operator_id}`}>
                <TableCell className="font-medium">{r.operator_name}</TableCell>
                <TableCell>{hm(r.programmed_minutes)}</TableCell>
                <TableCell className="font-medium">{hm(r.effective_minutes)}</TableCell>
                <TableCell className="text-muted-foreground">{hm(r.break_minutes)}</TableCell>
                <TableCell className={r.diff_minutes < 0 ? "text-destructive" : "text-[hsl(var(--hi-status-success))]"}>{r.diff_minutes >= 0 ? "+" : ""}{hm(r.diff_minutes)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{Object.entries(r.absences || {}).map(([k, v]) => `${k}: ${v}g`).join(", ") || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-0">
        <div className="p-3 font-heading text-lg font-semibold">Timbrature</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Data</TableHead><TableHead>Collaboratore</TableHead>
            <TableHead>Entrata</TableHead><TableHead>Uscita</TableHead>
            <TableHead>Effettive</TableHead><TableHead>Fonte</TableHead>{canWrite && <TableHead></TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {records.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessuna timbratura nel periodo.</TableCell></TableRow>}
            {records.map((r) => (
              <TableRow key={r.id} data-testid={`att-rec-${r.id}`}>
                <TableCell>{r.date}</TableCell>
                <TableCell className="font-medium">{r.operator_name || nameById[r.operator_id]}</TableCell>
                <TableCell>{r.clock_in ? fmtTime(r.clock_in) : "—"}</TableCell>
                <TableCell>{r.clock_out ? fmtTime(r.clock_out) : "—"}</TableCell>
                <TableCell>{hm(r.effective_minutes)}</TableCell>
                <TableCell className="text-xs capitalize text-muted-foreground">{r.source}{(r.corrections || []).length ? " · corretta" : ""}</TableCell>
                {canWrite && <TableCell><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`att-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!editRec} onOpenChange={(v) => !v && setEditRec(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">Correggi timbratura</DialogTitle></DialogHeader>
          {editRec && <div className="space-y-3">
            <div className="space-y-1.5"><Label>Giorno</Label>
              <Input type="date" value={editRec.edit_date || ""} onChange={(e) => setEditRec({ ...editRec, edit_date: e.target.value })} data-testid="edit-date" />
              <p className="text-[11px] text-muted-foreground">Proposto il giorno originale della timbratura. Puoi modificarlo se necessario.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Entrata</Label><Input type="time" step="60" value={editRec.in_time || ""} onChange={(e) => setEditRec({ ...editRec, in_time: e.target.value })} data-testid="edit-clock-in" /></div>
              <div className="space-y-1.5"><Label>Uscita</Label><Input type="time" step="60" value={editRec.out_time || ""} onChange={(e) => setEditRec({ ...editRec, out_time: e.target.value })} data-testid="edit-clock-out" /></div>
            </div>
            <div className="space-y-1.5"><Label>Motivo (facoltativo)</Label><Input value={editRec.reason || ""} onChange={(e) => setEditRec({ ...editRec, reason: e.target.value })} placeholder="Es. dimenticata timbratura uscita" data-testid="edit-reason" /></div>
            {(editRec.corrections || []).length > 0 && (
              <div className="rounded-md border border-border p-2.5 max-h-40 overflow-y-auto hi-scroll" data-testid="att-history">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Storico modifiche ({editRec.corrections.length})</div>
                <div className="space-y-1.5">
                  {[...editRec.corrections].reverse().map((c, i) => (
                    <div key={i} className="text-xs border-l-2 border-border pl-2">
                      <div><span className="capitalize font-medium">{c.field === "clock_in" ? "Entrata" : "Uscita"}</span>: {c.old ? fmtDateTime(c.old) : "non registrata"} → {c.new ? fmtDateTime(c.new) : "non registrata"}</div>
                      <div className="text-muted-foreground">{c.by || "?"} · {c.at ? fmtDateTime(c.at) : ""}{c.reason ? ` · Motivo: ${c.reason}` : " · Motivo: non specificato"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>}
          <DialogFooter><Button onClick={saveCorrection} data-testid="att-save-correction">Salva correzione</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
